import {
  type Job,
  JobLocationContext,
  JobShift,
  JobStatus,
  type JobWithRelations,
  PayRatePeriod,
} from '@factoryfixinc/ats-interfaces';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

export class JobUtil {
  static getLocation(job?: Job, options?: { format: 'short' }): string {
    const notFound = 'Unspecified Location';

    if (!job) {
      return notFound;
    }

    // Context-aware variants (null context reads as fixed_location). Local copy for now —
    // see docs/technical-design/.../address-formatting.md for the shared-formatter plan.
    if (job.locationContext === JobLocationContext.NATIONWIDE) {
      return 'Nationwide (U.S.)';
    }

    if (job.locationContext === JobLocationContext.LIVES_NEAR) {
      return job.city && job.state ? `near ${job.city}, ${job.state}` : notFound;
    }

    if (options?.format === 'short') {
      if (!job.state || !job.city) {
        return notFound;
      }

      return `${job.city}, ${job.state}`;
    }

    if (!job.state || !job.city) {
      return notFound;
    }

    // handle generically
    let addr = '';
    addr = this.appendWithSeparator(addr, job.street1);
    addr = this.appendWithSeparator(addr, job.city);
    addr = this.appendWithSeparator(addr, job.state);
    addr = this.appendWithSeparator(addr, job.postalCode, ' ');
    return addr || notFound;
  }

  static getShift(job?: Job): string {
    if (!job) {
      return 'No Shift';
    }

    if (job?.shift === null) {
      return 'No shift specified';
    }

    const shift = job.shift;
    if (shift === JobShift.FIRST_SHIFT) {
      return '1st Shift';
    } else if (shift === JobShift.SECOND_SHIFT) {
      return '2nd Shift';
    } else if (shift === JobShift.THIRD_SHIFT) {
      return '3rd Shift';
    } else if (shift === JobShift.SHORT_TERM_PROJECT) {
      return 'Short-term Project';
    } else if (shift === JobShift.MULTIPLE_SHIFTS) {
      return 'Multiple shifts';
    } else if (shift === JobShift.CUSTOM_STARTTIME_AND_ENDTIME) {
      const startTime = job.startTime;
      const endTime = job.endTime;

      if (startTime && endTime) {
        return `${this.formatTimeHelper(startTime)} - ${this.formatTimeHelper(endTime)}`;
      }
    }

    return 'No Shift';
  }

  static getHireType(job?: Job): string {
    return job?.hireType ? job.hireType : 'Unspecified Hire Type';
  }

  static getPayRange(job?: Job): string {
    if (!job) {
      return 'Undisclosed Pay Rate';
    }

    // Salary was added. If a job does not have a payPeriod set, default to hourly.
    if (job.payRatePeriod === PayRatePeriod.HOURLY || !job.payRatePeriod) {
      if (job.payRateMin && job.payRateMax) {
        return `$${job.payRateMin} - $${job.payRateMax} /hr`;
      }
      if (job.payRateMin) {
        return `$${job.payRateMin} /hr`;
      }
      if (job.payRateMax) {
        return `$${job.payRateMax} /hr`;
      }
    }

    if (job.payRatePeriod === PayRatePeriod.ANNUAL) {
      if (job.payRateSalaryMin && job.payRateSalaryMax) {
        return `${this.shortenSalary(job.payRateSalaryMin)} - ${this.shortenSalary(
          job.payRateSalaryMax,
        )} /yr`;
      }
      if (job.payRateSalaryMin) {
        return `${this.shortenSalary(job.payRateSalaryMin)} /yr`;
      }
      if (job.payRateSalaryMax) {
        return `${this.shortenSalary(job.payRateSalaryMax)} /yr`;
      }
    }

    return 'Undisclosed Pay Rate';
  }

  static getJobLiveStatusDuration(
    job?: JobWithRelations<'jobStatusHistories'>,
  ): string | undefined {
    if (!job?.jobStatusHistories?.length) {
      return;
    }

    const liveDate = job.jobStatusHistories
      .sort((a, b) => new Date(b.createTs).getTime() - new Date(a.createTs).getTime())
      .find((history) => history.status === JobStatus.LIVE)?.createTs;

    if (!liveDate) {
      return;
    }

    const timeAgo = dayjs().to(dayjs(liveDate));
    const date = dayjs(liveDate).format('LL');

    return `${timeAgo} (${date})`;
  }

  private static formatTimeHelper(time: string): string {
    const currentDate = dayjs().format('YYYY-MM-DD');
    const dateTimeString = `${currentDate} ${time}`;

    return dayjs(dateTimeString, 'YYYY-MM-DD h:mmA').format('LT');
  }

  private static shortenSalary(salary: number): string {
    return `$${Math.round(salary / 1000)}k`;
  }

  private static appendWithSeparator(base: string, item?: string, separator = ', '): string {
    if (!base.length) {
      return item ?? '';
    }

    if (item) {
      return base + separator + item;
    } else {
      return base;
    }
  }
}
